import { useState } from 'react'
import { AssetReference } from '@opallabs/cassini'
import { kuiperClassNames } from '@opallabs/kuiper'
import { ModularCarousel } from '../../../ModularCarousel'
import { doNothing } from '../../helpers/functions/doNothing'
import getAssetOrientation from '../../helpers/functions/getAssetOrientation'
import { useValidModules } from '../../helpers/hooks'
import { Module, OnDragDropProps } from '../../helpers/types'
import { getCarouselLinkAdModule } from './getCarouselLinkAdModule'
import { MobileStream } from './MobileStream'
import { styles } from './styles'

const getAssetHeight = (asset: AssetReference) => {
  let orientation = 'square'
  let assetHeight = 375
  if (asset) {
    orientation = getAssetOrientation(asset)
    if (orientation === 'landscape') {
      assetHeight = 196
    } else if (orientation === 'portrait') {
      assetHeight = 469
    }
  }
  return { orientation, assetHeight }
}

type Props = {
  accountImage: AssetReference
  accountUsername: string
  content: string
  isExpanded: boolean
  isPresenting: boolean
  linkAdCta: string
  location: string
  modules: Module[]
  notCropped: boolean
  onAddSection?: () => void
  onDeleteSection?: () => void
  onDragDrop?: OnDragDropProps
  onExpandAsset?: () => void
  onRemoveAsset?: () => void
  onShowAssetPicker?: () => void
  postType: string
  readOnly: boolean
  truncate: boolean
  updateSectionPostOption?: (index: number, value: string, dest: string) => void
  updateText?: (val: string, dest: string) => void
}

export const InstagramCarouselLinkAd: React.FC<Props> = ({
  accountImage,
  accountUsername,
  content,
  isExpanded,
  isPresenting = false,
  linkAdCta,
  location,
  modules = [],
  notCropped,
  onAddSection,
  onDeleteSection,
  onDragDrop,
  onExpandAsset,
  onRemoveAsset,
  onShowAssetPicker,
  postType,
  readOnly = false,
  truncate,
  updateSectionPostOption,
  updateText,
}) => {
  const [height, setHeight] = useState(670)
  const firstAsset =
    modules && modules[0] && (modules[0].igCarouselAdAsset as AssetReference)
  const { orientation, assetHeight } = getAssetHeight(firstAsset)

  const validModules = useValidModules({
    modules,
    onAddSection: onAddSection ? onAddSection : doNothing,
    componentNames: ['ig_car_ad_section'],
    minModulesLength: 1,
  })

  const availableModules = [
    {
      component: MobileStream,
      componentName: 'ig_car_ad_section',
      title: '',
    },
  ]

  const onContentChange = (val: string, dest: string) => {
    if (!updateText || !updateSectionPostOption) return
    if (!isNaN(+dest)) updateSectionPostOption(+dest, val, 'headline')
    else updateText(val, 'content')
  }

  const onFooterResize = (footerHeight: number) => {
    const viewportHeight = notCropped ? assetHeight : 375
    const newHeight = 150 + viewportHeight + footerHeight
    setHeight(newHeight)
  }

  return (
    <div
      css={styles.getBaseStyles(isExpanded, height, orientation, notCropped)}
      className={kuiperClassNames(
        'instagram instagram-carousel-link-ad instagram-mobile-stream'
      )}
    >
      <ModularCarousel
        addComponentText="Add Carousel Section"
        allProps={{
          accountImage,
          accountUsername,
          content,
          cta: linkAdCta,
          assetHeight,
          notCropped,
          onContentChange,
          onFooterResize,
          postType,
          truncate,
          location,
        }}
        overrideStyles={{
          activeViewportStyles: styles.getActiveViewport,
          arrowStyles: () => styles.getArrowHeight(notCropped, orientation),
          viewportStyles: () =>
            styles.getViewport(readOnly, notCropped, assetHeight),
          sectionStyles: styles.getSection,
        }}
        getModule={getCarouselLinkAdModule}
        isExpanded={isExpanded}
        availableModules={availableModules}
        carouselWidth={375}
        modules={validModules}
        maxSections={10}
        onDragDrop={onDragDrop ? onDragDrop : doNothing}
        onAddSection={onAddSection ? onAddSection : doNothing}
        onDeleteSection={onDeleteSection ? onDeleteSection : doNothing}
        onExpandAsset={onExpandAsset ? onExpandAsset : doNothing}
        onRemoveAsset={onRemoveAsset ? onRemoveAsset : doNothing}
        onShowAssetPicker={onShowAssetPicker ? onShowAssetPicker : doNothing}
        updateSectionPostOption={
          updateSectionPostOption ? updateSectionPostOption : doNothing
        }
        readOnly={readOnly}
        isPresenting={isPresenting}
      />
    </div>
  )
}
